import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection
} from '@angular/core';
import { provideRouter, RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LayoutStore } from '@twist/ui-core';
import { PageTitleStrategy } from './services/page-title-strategy.service';
import { AuthenticationApiConfiguration, AuthenticationApiModule } from '@twist/backbone-api-services/authentication';
import { InstallationsApiConfiguration, InstallationsApiModule } from '@twist/backbone-api-services/installations';
import { ManufacturingApiConfiguration, ManufacturingApiModule } from '@twist/backbone-api-services/manufacturing';
import { environment } from '../environments';

import { InfrastructureApiConfiguration, InfrastructureApiModule } from '@twist/backbone-api-services/infrastructure';
import { AuthStore, CoreModule } from '@twist/core';
import { IconStore } from '@twist/icons';
import { Logger, provideLogging } from '@twist/logging';

import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { PathPreserveQueryLocationStrategy } from './services/path-preserve-query-location-strategy';
import { LocationStrategy } from '@angular/common';
import {
  HardwareBuildupApiConfiguration,
  HardwareBuildupApiModule
} from '@twist/backbone-api-services/hardware-buildup';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_RIPPLE_GLOBAL_OPTIONS, provideNativeDateAdapter, RippleGlobalOptions } from '@angular/material/core';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { AiAssistantApiConfiguration, AiAssistantApiModule } from '@twist/backbone-api-services/ai-assistant';
import { CiamApiConfiguration, CiamApiModule } from '@twist/backbone-api-services/ciam';
import { CONTEXT_ORGANIZATION_KEY, CONTEXT_USER_ID } from '@twist/ciam-core';
import { ProductsApiConfiguration, ProductsApiModule } from '@twist/backbone-api-services/products';
import { AnalyticsApiConfiguration, AnalyticsApiModule } from '@twist/backbone-api-services/analytics';
import { LocoApiConfiguration, LocoApiModule } from '@twist/backbone-api-services/loco';
import { DevicesApiConfiguration, DevicesApiModule } from '@twist/backbone-api-services/devices';
import { MetadataApiConfiguration, MetadataApiModule } from '@twist/backbone-api-services/metadata';
import {
  PlatformDefinitionsApiConfiguration,
  PlatformDefinitionsApiModule
} from '@twist/backbone-api-services/platform-definitions';
import { CustomReuseStrategy } from './services/custom-route-reuse-strategy';
import { TWIST_LAYOUT_ENVIRONMENT } from '@twist/ui-layout';
import { NAV_DESCRIPTOR, provideRegistryNavSource } from '@twist/ui-navigation';
//import { analyticsNav } from './nav/analytics.nav';
import { installationsNav } from './nav/installations.nav';
import { devicesNav } from './nav/devices.nav';
import { metadataNav } from './nav/metadata.nav';
import { productsNav } from './nav/products.nav';
import { myOrganizationSecurityNav } from './nav/my-organization-security.nav';
import { manufacturingNav } from './nav/manufacturing.nav';
import { definitionsNav } from './nav/definitions.nav';
import { adminSecurityNav } from './nav/admin-security.nav';
import { summariesNav } from './nav/summaries.nav';

// angular material - Disable teh ripple effect
const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 300,
    exitDuration: 0
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    provideLogging(environment),
    provideRouter(appRoutes),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    // TWS-98: warm the icon cache (ui + status groups) on app boot so initial
    // chrome icons render without per-icon round-trips. Failures here are
    // logged but MUST NOT reject bootstrap — icons resolve lazily on demand
    // when the prefetch fails, so the portal still renders.
    provideAppInitializer(() => {
      const store = inject(IconStore);
      const logger = inject(Logger);
      return store.prefetchEssentials().catch((err: unknown) => {
        logger.error(
          'IconStore',
          'Icon prefetch failed during bootstrap; continuing without prefetched icons',
          err
        );
        // Swallow — non-critical bootstrap failures must never reject the app.
      });
    }),
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    // Required by `tw-datepicker` (@twist/ui-form) — wraps the Material
    // datepicker. Uses the browser's native Date adapter (no moment / Luxon).
    provideNativeDateAdapter(),
    provideOAuthClient(),
    provideCharts(withDefaultRegisterables()),
    // TWS-134 W10.2: `provideEchartsCore` removed alongside ngx-echarts.
    // `EchartsBaseComponent` now imports the full `echarts` module directly
    // (tree-shaking handled by the bundler) and initialises chart instances
    // itself, so no DI registration is needed.
    { provide: LocationStrategy, useClass: PathPreserveQueryLocationStrategy },
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    { provide: TWIST_LAYOUT_ENVIRONMENT, useValue: { documentationRootUrl: environment.documentationRootUrl } },
    { provide: CONTEXT_ORGANIZATION_KEY, useFactory: () => inject(AuthStore).organizationKey() },
    { provide: CONTEXT_USER_ID, useFactory: () => inject(AuthStore).userId() },
    { provide: NAV_DESCRIPTOR, useValue: installationsNav, multi: true },
    //{ provide: NAV_DESCRIPTOR, useValue: analyticsNav, multi: true },
    { provide: NAV_DESCRIPTOR, useValue: devicesNav, multi: true },
    { provide: NAV_DESCRIPTOR, useValue: metadataNav, multi: true },
    { provide: NAV_DESCRIPTOR, useValue: productsNav, multi: true },
    { provide: NAV_DESCRIPTOR, useValue: myOrganizationSecurityNav, multi: true },
    { provide: NAV_DESCRIPTOR, useValue: manufacturingNav, multi: true },
    { provide: NAV_DESCRIPTOR, useValue: definitionsNav, multi: true },
    { provide: NAV_DESCRIPTOR, useValue: adminSecurityNav, multi: true },
    { provide: NAV_DESCRIPTOR, useValue: summariesNav, multi: true },
    provideRegistryNavSource(),
    //{ provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    LayoutStore,
    importProvidersFrom(
      CoreModule.forRoot(environment),
      AuthenticationApiModule.forRoot(() => {
        return new AuthenticationApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      InstallationsApiModule.forRoot(() => {
        return new InstallationsApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      ProductsApiModule.forRoot(() => {
        return new ProductsApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      AnalyticsApiModule.forRoot(() => {
        return new AnalyticsApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      MetadataApiModule.forRoot(() => {
        return new MetadataApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      PlatformDefinitionsApiModule.forRoot(() => {
        return new PlatformDefinitionsApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      DevicesApiModule.forRoot(() => {
        return new DevicesApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      ManufacturingApiModule.forRoot(() => {
        return new ManufacturingApiConfiguration({
          basePath: environment.manufacturingUrl
        });
      }),
      HardwareBuildupApiModule.forRoot(() => {
        return new HardwareBuildupApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      InfrastructureApiModule.forRoot(() => {
        return new InfrastructureApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      AiAssistantApiModule.forRoot(() => {
        return new AiAssistantApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      LocoApiModule.forRoot(() => {
        return new LocoApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      CiamApiModule.forRoot(() => {
        return new CiamApiConfiguration({
          basePath: environment.backboneUrl
        });
      }),
      MonacoEditorModule.forRoot()
    )
  ]
};
